import * as Api from '@ViewModels';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUserQuery } from '../../../queries';
import { useCompanyTagFilters } from '../../components/AdvancedFilters/hooks/useCompanyTagFilters';
import { useDateRangeFilter } from '../../components/AdvancedFilters/hooks/useDateRangeFilter';
import { useKeyFactFilters } from '../../components/AdvancedFilters/hooks/useKeyFactFilters';
import { useLocationFilters } from '../../components/AdvancedFilters/hooks/useLocationFilters';
import { useStatusFilters } from '../../components/AdvancedFilters/hooks/useStatusFilters';
import { useTagFilters } from '../../components/AdvancedFilters/hooks/useTagFilters';

interface AdvancedFiltersContext {
	keyFactFilters: ReturnType<typeof useKeyFactFilters>;
	tagFilters: ReturnType<typeof useTagFilters>;
	createDateFilters: ReturnType<typeof useDateRangeFilter>;
	lastCommunicationDateFilters: ReturnType<typeof useDateRangeFilter>;
	statusFilters: ReturnType<typeof useStatusFilters>;
	locationFilters: ReturnType<typeof useLocationFilters>;
	companyTagFilters: ReturnType<typeof useCompanyTagFilters>;
	cancelChanges: () => void;
	saveChanges: () => void;
	onSave: (
		filterCriteria: Api.IContactFilterCriteria[],
		householdGrouping?: boolean,
		ownershipFilter?: Api.IOwnershipFilter
	) => void;
	householdGroupingOn?: boolean;
	setHouseholdGroupingOn?: (value: boolean) => void;
	ownershipFilter?: Api.IOwnershipFilter;
	setOwnershipFilter?: (value: Api.IOwnershipFilter) => void;
	selectedUserId?: string;
	setSelectedUserId?: (value: string) => void;
}

const AdvancedFiltersContext = createContext<AdvancedFiltersContext>(null);

export interface IAdvancedFiltersProps {
	initialFilterCriteria?: Api.IContactFilterCriteria[];
	initialOwnershipFilter?: Api.IOwnershipFilter;
	onSave: (
		filterCriteria: Api.IContactFilterCriteria[],
		householdGrouping?: boolean,
		ownershipFilter?: Api.IOwnershipFilter
	) => void;
	refresh?: boolean;
	initialHouseholdGroupingOn?: boolean;
	showSelectAUserOption?: boolean;
	initialSelectedUserId?: string;
	title?: string;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 *
 * @param refresh pass the same isOpen arg as the AdvancedFiltersModal to refresh filters on open
 * @param impersonationContext if passed here and using the LegacyUI, impersonationContext should also be passed there
 */
export const AdvancedFiltersContextProvider: FC<React.PropsWithChildren<IAdvancedFiltersProps>> = ({
	children,
	initialFilterCriteria = [],
	initialOwnershipFilter,
	onSave,
	refresh,
	initialHouseholdGroupingOn = false,
	initialSelectedUserId,
	impersonationContext,
}) => {
	const statusFilters = useStatusFilters(initialFilterCriteria);
	const { findMatchingStatus, selectedStatuses, getStatusCriteria, refreshStatuses } = statusFilters;

	const keyFactFilters = useKeyFactFilters(initialFilterCriteria);
	const {
		findMatchingKeyFact,
		selectedKeyFact,
		getKeyFactCriteria,
		cancelChanges: cancelKeyFactChanges,
		refreshKeyFacts,
	} = keyFactFilters;

	const createDateFilters = useDateRangeFilter(Api.ContactFilterCriteriaProperty.CreationDate, initialFilterCriteria);
	const lastCommunicationDateFilters = useDateRangeFilter(
		Api.ContactFilterCriteriaProperty.LastCommunicationDate,
		initialFilterCriteria
	);

	const [householdGroupingOn, setHouseholdGroupingOn] = useState<boolean>(initialHouseholdGroupingOn);
	const [selectedUserId, setSelectedUserId] = useState<string>(initialSelectedUserId);
	const [ownershipFilter, setOwnershipFilter] = useState<Api.IOwnershipFilter>(initialOwnershipFilter);

	useEffect(() => {
		setOwnershipFilter(initialOwnershipFilter);
		setSelectedUserId(initialSelectedUserId);
	}, [initialOwnershipFilter, initialSelectedUserId]);

	useEffect(() => {
		setHouseholdGroupingOn(initialHouseholdGroupingOn);
	}, [initialHouseholdGroupingOn]);

	const userQuery = useUserQuery({
		enabled: Boolean(selectedUserId),
		id: selectedUserId ?? '',
		impersonationContext,
	});
	useEffect(() => {
		if (userQuery.data) {
			setSelectedUserId(userQuery.data.id);
		}
	}, [userQuery.data]);

	const tagFilters = useTagFilters({ initialFilterCriteria });
	const { isTagCriteria, toCriteria, cancelChanges: cancelTagChanges, refreshTags } = tagFilters;

	const locationFilters = useLocationFilters(initialFilterCriteria);
	const { getLocationCriteria, refreshLocationFilters, isLocationCriteria } = locationFilters;

	const companyTagFilters = useCompanyTagFilters(initialFilterCriteria);
	const { getCompanyTagCriteria, refreshCompanyTagFilters, isCompanyTagCriteria } = companyTagFilters;

	useEffect(() => {
		if (refresh) {
			refreshStatuses(initialFilterCriteria ?? null);
			refreshTags(initialFilterCriteria);
			refreshKeyFacts(initialFilterCriteria);
			createDateFilters.refreshDateFilter(initialFilterCriteria);
			lastCommunicationDateFilters.refreshDateFilter(initialFilterCriteria);
			refreshLocationFilters(initialFilterCriteria);
			refreshCompanyTagFilters(initialFilterCriteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refresh]);

	const cancelChanges = useCallback(() => {
		cancelTagChanges();
		cancelKeyFactChanges();
		createDateFilters.cancelDateChanges();
		lastCommunicationDateFilters.cancelDateChanges();
	}, [createDateFilters, lastCommunicationDateFilters, cancelKeyFactChanges, cancelTagChanges]);

	const extraCriteria = useMemo(() => {
		return initialFilterCriteria.filter(criterion => {
			const isTagFilter = isTagCriteria(criterion);
			const isStatusFilter = !!findMatchingStatus(criterion);
			const isKeyFactFilter = !!findMatchingKeyFact(criterion);
			const isCreationDateFilter = !!createDateFilters.findMatchingDateFilter(
				Api.ContactFilterCriteriaProperty.CreationDate,
				criterion
			);
			const isLastCommunicationDateFilter = !!createDateFilters.findMatchingDateFilter(
				Api.ContactFilterCriteriaProperty.LastCommunicationDate,
				criterion
			);
			const isLocationFilter = isLocationCriteria(criterion);
			const isCompanyTagFilter = isCompanyTagCriteria(criterion);

			return (
				!isTagFilter &&
				!isStatusFilter &&
				!isKeyFactFilter &&
				!isCreationDateFilter &&
				!isLastCommunicationDateFilter &&
				!isLocationFilter &&
				!isCompanyTagFilter
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialFilterCriteria]);

	const saveChanges = useCallback(() => {
		const newCriteria: Api.IContactFilterCriteria[] = [];
		const tagCriteria = toCriteria();
		if (tagCriteria) {
			newCriteria.push(...tagCriteria);
		}
		if (selectedStatuses && selectedStatuses.length) {
			const allStatusCriteria = getStatusCriteria();
			newCriteria.push(...allStatusCriteria);
		}
		if (selectedKeyFact !== null) {
			const keyFactCriteria = getKeyFactCriteria();
			newCriteria.push(keyFactCriteria);
		}
		if (createDateFilters.startDate || createDateFilters.endDate) {
			const createDateCriteria = createDateFilters.getDateCriteria();
			newCriteria.push(createDateCriteria);
		}
		if (
			lastCommunicationDateFilters.startDate ||
			lastCommunicationDateFilters.endDate ||
			lastCommunicationDateFilters.relativeDate
		) {
			const lastCommunicationDateCriteria = lastCommunicationDateFilters.getDateCriteria();
			newCriteria.push(lastCommunicationDateCriteria);
		}
		const locationCriteria = getLocationCriteria();
		if (locationCriteria.length > 0) {
			newCriteria.push(...locationCriteria);
		}
		const companyTagCriteria = getCompanyTagCriteria();
		if (companyTagCriteria.length > 0) {
			newCriteria.push(...companyTagCriteria);
		}
		if (extraCriteria && extraCriteria.length > 0) {
			extraCriteria.forEach(criterion => {
				newCriteria.push(criterion);
			});
		}
		onSave(newCriteria, householdGroupingOn, ownershipFilter);
	}, [
		createDateFilters,
		lastCommunicationDateFilters,
		extraCriteria,
		getCompanyTagCriteria,
		getKeyFactCriteria,
		getLocationCriteria,
		getStatusCriteria,
		householdGroupingOn,
		onSave,
		ownershipFilter,
		selectedKeyFact,
		selectedStatuses,
		toCriteria,
	]);

	return (
		<AdvancedFiltersContext.Provider
			value={{
				statusFilters,
				keyFactFilters,
				tagFilters,
				locationFilters,
				companyTagFilters,
				createDateFilters,
				lastCommunicationDateFilters,
				cancelChanges,
				saveChanges,
				onSave,
				householdGroupingOn,
				setHouseholdGroupingOn,
				ownershipFilter,
				setOwnershipFilter,
				selectedUserId,
				setSelectedUserId,
			}}
		>
			{children}
		</AdvancedFiltersContext.Provider>
	);
};

export const useAdvancedFiltersContext = () => {
	const context = useContext(AdvancedFiltersContext);
	if (!context) {
		throw new Error('useAdvancedFiltersContext must be used within a AdvancedFiltersProvider');
	}
	return context;
};
